import { Fragment, type ReactNode } from "react";
import { highlightCode, isHighlightable } from "./highlight.tsx";

// A deliberately small renderer for drill statements: headings, bullets, bold
// and inline code. It builds React elements rather than HTML, so a statement can
// never inject markup, and there is no markdown dependency to keep current for
// what amounts to four constructs.

function inline(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  // Split on `code` and **bold**, keeping the delimiters via a capturing group.
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let index = 0;
  while ((match = pattern.exec(text)) != null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${index++}`;
    if (token.startsWith("`")) {
      parts.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else {
      // Bold is rendered by recursing, because a requirement bullet almost
      // always names the thing it is about in code inside the bold run. Slicing
      // the contents out as plain text printed the backticks on screen.
      // The recursion terminates: the bold pattern excludes asterisks, so the
      // inner pass can only find code spans.
      parts.push(<strong key={key}>{inline(token.slice(2, -2), key)}</strong>);
    }
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function Markdown({ source }: { source: string }) {
  const blocks: ReactNode[] = [];
  const lines = source.split("\n");
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length === 0) return;
    const items = bullets;
    bullets = [];
    blocks.push(
      <ul key={`ul-${blocks.length}`}>
        {items.map((item, i) => (
          <li key={i}>{inline(item, `li-${blocks.length}-${i}`)}</li>
        ))}
      </ul>,
    );
  };

  // Fenced code blocks are tracked with a flag rather than a lookahead, because
  // their contents must bypass every other rule: indentation, blank lines and
  // any `-` or `#` inside a code sample have to survive verbatim.
  let fenced: string[] | null = null;
  // The fence's info string, e.g. the "ts" in ```ts. Kept rather than discarded
  // so a code fence can be syntax highlighted and a plain one cannot.
  let language = "";

  const flushFence = () => {
    if (fenced === null) return;
    const code = fenced.join("\n");
    fenced = null;
    blocks.push(
      <pre key={`code-${blocks.length}`} className="sample">
        {isHighlightable(language) ? highlightCode(code) : code}
      </pre>,
    );
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith("```")) {
      if (fenced === null) {
        flushBullets();
        language = trimmed.slice(3).trim();
        fenced = [];
      } else {
        flushFence();
      }
      continue;
    }
    if (fenced !== null) {
      // Raw, not trimmed: leading whitespace is meaningful in a code sample.
      fenced.push(line);
      continue;
    }

    if (trimmed.startsWith("- ")) {
      bullets.push(trimmed.slice(2));
      continue;
    }
    flushBullets();
    if (trimmed === "") continue;
    if (trimmed.startsWith("## ")) {
      blocks.push(<h3 key={`h-${blocks.length}`}>{inline(trimmed.slice(3), `h-${blocks.length}`)}</h3>);
    } else {
      blocks.push(<p key={`p-${blocks.length}`}>{inline(trimmed, `p-${blocks.length}`)}</p>);
    }
  }
  flushBullets();
  // An unclosed fence still renders what it collected rather than swallowing the
  // rest of the statement silently.
  if (fenced !== null && fenced.length > 0) flushFence();

  return <Fragment>{blocks}</Fragment>;
}
